import { Link } from 'react-router-dom'
import { ExternalLink, Shield } from 'lucide-react'
import { featuredPartners, partnersConfig, partnerPath } from '../data/partners'

type Tone = 'warm' | 'dark'

/**
 * Affiliate-first partner strip. Primary monetization path. Never nags to
 * disable ad blockers — partner /go links still work.
 *
 * Labeled "Affiliate links" with a commission disclosure, and kept separate
 * from Google AdSense units (which carry their own "Advertisement" label in
 * <AdSenseSlot>). Never render an AdSense unit inside this block.
 */
export function PartnerSupportStrip({
  tone = 'dark',
  className = '',
}: {
  tone?: Tone
  className?: string
}) {
  const isWarm = tone === 'warm'
  const cards = featuredPartners.slice(0, 6)

  return (
    <section
      className={`rounded-2xl border p-6 sm:p-8 ${
        isWarm
          ? 'border-warm-peach/50 bg-white/70 text-warm-navy'
          : 'border-white/10 bg-white/5 text-white'
      } ${className}`}
      aria-label="Partner protection picks (affiliate links)"
      data-partner-support-strip
    >
      <p
        className={`mb-3 text-[11px] font-semibold uppercase tracking-wider ${isWarm ? 'text-warm-navy/55' : 'text-indigo-300/70'}`}
        data-affiliate-label
      >
        Affiliate links
      </p>
      <div className="flex gap-3 mb-4">
        <Shield
          className={`w-6 h-6 flex-shrink-0 mt-0.5 ${isWarm ? 'text-warm-coral' : 'text-cyan-400'}`}
          aria-hidden
        />
        <div>
          <h2 className={`text-lg sm:text-xl font-semibold ${isWarm ? 'font-warm-display text-warm-navy' : ''}`}>
            {partnersConfig.headline}
          </h2>
          <p className={`mt-2 text-sm leading-relaxed ${isWarm ? 'text-warm-navy/70' : 'text-indigo-200/80'}`}>
            {partnersConfig.supportLine}
          </p>
        </div>
      </div>

      <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {cards.map((partner) => (
          <li key={partner.slug}>
            <a
              href={partnerPath(partner.slug)}
              target="_blank"
              rel="noopener noreferrer sponsored"
              className={`group flex h-full flex-col gap-2 rounded-xl border p-4 transition-colors ${
                isWarm
                  ? 'border-warm-peach/60 bg-warm-cream/80 hover:border-warm-coral/50 hover:bg-white'
                  : 'border-white/10 bg-white/[0.04] hover:border-cyan-500/30 hover:bg-white/[0.07]'
              }`}
              data-partner-slug={partner.slug}
            >
              <span className="flex items-center justify-between gap-2">
                <span className={`font-semibold ${isWarm ? 'text-warm-navy' : 'text-white'}`}>
                  {partner.name}
                </span>
                <ExternalLink
                  className={`w-4 h-4 opacity-60 group-hover:opacity-100 ${isWarm ? 'text-warm-coral' : 'text-cyan-300'}`}
                  aria-hidden
                />
              </span>
              <span className={`text-sm leading-snug ${isWarm ? 'text-warm-navy/65' : 'text-indigo-200/75'}`}>
                {partner.reason}
              </span>
              <span className={`text-[11px] ${isWarm ? 'text-warm-navy/45' : 'text-indigo-300/60'}`}>
                Affiliate link
              </span>
            </a>
          </li>
        ))}
      </ul>

      <p className={`mt-4 text-xs leading-relaxed ${isWarm ? 'text-warm-navy/55' : 'text-indigo-200/60'}`}>
        {partnersConfig.disclosure}{' '}
        <Link to="/privacy#affiliate-links" className="underline">
          How affiliate links work
        </Link>
      </p>
    </section>
  )
}
